import { mergeEstimatePdfImages } from '@/lib/sales/prepare-pdf-line-images';
/**
 * Public PDF estimate viewer.
 * Fetches the estimate via the public API and renders it with @react-pdf/renderer
 * so the customer (including from the portal) can download or print the tenant's
 * estimate document. Mirrors pdf-invoice.$id.tsx.
 */
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Loader2, FileText } from 'lucide-react';
import { PDFViewer } from '@react-pdf/renderer';
import {
  type PdfCompanyInfo,
  type PdfCustomFieldValue,
} from '@/components/documents/estimates/pdfTypes';
import { EstimateDocumentPdf } from '@/components/documents/estimates/EstimateDocumentPdf';
import { DocumentNotFound } from '@/components/public/DocumentNotFound';
import { AutoPrintPdf } from '@/components/documents/AutoPrintPdf';
import type { Estimate } from '@/types/estimate';
import type { PublicWisetackCta } from '@/types/public';
import type { EstimateDocLayout } from '@/types/estimateDocTemplate';

const OPS_URL = import.meta.env.VITE_OPERATIONS_URL ?? '';

/** The public estimate context (same assembler the quote page reads from). */
interface PublicEstimateResponse {
  estimate: Estimate;
  company?: PdfCompanyInfo | null;
  customFields?: PdfCustomFieldValue[];
  serviceFee?: { show: boolean; percent: number; chargedAmount?: number | null } | null;
  wisetack?: PublicWisetackCta | null;
  timezone?: string | null;
  /** Resolved document-template layout — when present, the PDF renders through it. */
  resolvedDocLayout?: EstimateDocLayout | null;
  /** The technician who prepared the estimate (single-scope "Prepared by"). */
  presentedBy?: { name?: string | null } | null;
}

export const Route = createFileRoute('/(public)/pdf-estimate/$id')({
  // `?print=1` (opened by the portal's Print action) auto-prints the document.
  validateSearch: (search: Record<string, unknown>) => ({
    print: search.print === '1' || search.print === true || search.print === 1,
  }),
  component: PdfEstimatePage,
});

function PdfEstimatePage() {
  const { id } = Route.useParams();
  const { print } = Route.useSearch();

  const { data, isLoading, isError } = useQuery<PublicEstimateResponse>({
    queryKey: ['public', 'estimate-pdf', id],
    queryFn: async () => {
      const res = await fetch(
        `${OPS_URL}/estimates/public/${id}?includeLineItemImageDataUri=true`,
        {
          headers: { 'Content-Type': 'application/json' },
        },
      );
      if (!res.ok) throw new Error('Estimate not found');
      const data: PublicEstimateResponse = await res.json();
      return { ...data, estimate: mergeEstimatePdfImages(data.estimate, data.estimate) };
    },
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center gap-2 text-gray-500">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="text-sm">Loading estimate…</span>
      </div>
    );
  }

  if (isError || !data?.estimate) {
    return <DocumentNotFound message="Estimate not found" />;
  }

  const est = data.estimate;
  const doc = (
    <EstimateDocumentPdf
      estimate={est}
      company={data.company ?? null}
      customFields={data.customFields}
      wisetack={data.wisetack ?? null}
      serviceFee={data.serviceFee ?? null}
      timeZone={data.timezone ?? null}
      resolvedDocLayout={data.resolvedDocLayout ?? null}
      presentedByName={data.presentedBy?.name ?? null}
    />
  );

  return (
    <div className="flex min-h-screen flex-col">
      <div className="flex h-12 items-center gap-2 border-b bg-white px-4 shadow-sm">
        <FileText className="h-4 w-4 text-gray-500" />
        <span className="text-sm font-medium text-gray-700">
          {est.estimateNumber ? `Estimate #${est.estimateNumber}` : 'Estimate'}
        </span>
        {data.company?.name ? (
          <span className="text-sm text-gray-400">· {data.company.name}</span>
        ) : null}
      </div>

      <div className="flex-1">
        <PDFViewer
          width="100%"
          height="100%"
          style={{ border: 'none', minHeight: 'calc(100vh - 48px)' }}
        >
          {doc}
        </PDFViewer>
      </div>
      {print ? <AutoPrintPdf document={doc} /> : null}
    </div>
  );
}
